// Componente principal de la aplicación PinoHome
// Configuración de React Router y rutas principales

import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { CartProvider } from './contexts/CartContext'
import { BusinessSettingsProvider } from './contexts/BusinessSettingsContext'
import { BusinessRulesProvider } from './contexts/BusinessRulesContext'
import { NotificationsSettingsProvider } from './contexts/NotificationsSettingsContext'
import AdminLayout from './components/admin/AdminLayout'
import ProtectedRoute from './components/ProtectedRoute'
import PublicLayout from './components/public/PublicLayout'
import Login from './pages/Login'
import PublicHome from './pages/public/PublicHome'
import PublicProducts from './pages/public/PublicProducts'
import PublicProductDetail from './pages/public/PublicProductDetail'
import PublicCart from './pages/public/PublicCart'
import PublicCheckout from './pages/public/PublicCheckout'
import PublicOffers from './pages/public/PublicOffers'
import PublicContact from './pages/public/PublicContact'
import OrderConfirmation from './pages/OrderConfirmation'
import MyOrders from './pages/MyOrders'
import MyOrderDetail from './pages/MyOrderDetail'
import PublicProfile from './pages/public/PublicProfile'
import CustomerProfile from './pages/CustomerProfile'
import Help from './pages/Help'
import AdminDashboard from './pages/admin/AdminDashboard'
import AdminProducts from './pages/admin/AdminProducts'
import ProductFormRouter from './pages/admin/ProductFormRouter'
import AdminSettings from './pages/admin/AdminSettings'
import AdminCategories from './pages/admin/AdminCategories'
import AdminSubcategories from './pages/admin/AdminSubcategories'
import AdminHelpSettings from './pages/admin/AdminHelpSettings'
import AdminHomeSettings from './pages/admin/AdminHomeSettings'
import AdminShipping from './pages/admin/AdminShipping'
import AdminProfitSettings from './pages/admin/AdminProfitSettings'
import AdminBusinessSettings from './pages/admin/AdminBusinessSettings'
import AdminBusinessRules from './pages/admin/AdminBusinessRules'
import AdminNotificationsSettings from './pages/admin/AdminNotificationsSettings'
import AdminAuditLogs from './pages/admin/AdminAuditLogs'
import AdminSuppliers from './pages/admin/AdminSuppliers'
import SupplierForm from './pages/admin/SupplierForm'
import SupplierDetail from './pages/admin/SupplierDetail'
import AdminOrders from './pages/admin/AdminOrders'
import OrderDetail from './pages/admin/OrderDetail'
import AdminCustomers from './pages/admin/AdminCustomers'
import CustomerDetail from './pages/admin/CustomerDetail'
import AdminUsers from './pages/admin/AdminUsers'
import UserForm from './pages/admin/UserForm'
import AdminSecurity from './pages/admin/AdminSecurity'
import AdminWarehouses from './pages/admin/AdminWarehouses'
import WarehouseForm from './pages/admin/WarehouseForm'
import AdminStock from './pages/admin/AdminStock'
import StockMovements from './pages/admin/StockMovements'
import StockTransfer from './pages/admin/StockTransfer'
import AdminPurchaseOrders from './pages/admin/AdminPurchaseOrders'
import PurchaseOrderForm from './pages/admin/PurchaseOrderForm'
import PurchaseOrderDetail from './pages/admin/PurchaseOrderDetail'
import RegisterPayment from './pages/admin/RegisterPayment'
import AdminSuppliesList from './pages/admin/AdminSuppliesList'
import SupplyForm from './pages/admin/SupplyForm'
import AdminAlertsSettings from './pages/admin/AdminAlertsSettings'
import ProductPriceHistory from './pages/admin/ProductPriceHistory'
import AdminOffers from './pages/admin/AdminOffers'
import OfferForm from './pages/admin/OfferForm'
import OfferDetail from './pages/admin/OfferDetail'
import AdminSales from './pages/admin/AdminSales'
import SaleForm from './pages/admin/SaleForm'
import SaleDetail from './pages/admin/SaleDetail'
import AdminDataManagement from './pages/admin/AdminDataManagement'
import AdminAccounting from './pages/admin/AdminAccounting'
import AdminLabels from './pages/admin/AdminLabels'
import './App.css'

/**
 * Componente App - Componente raíz de la aplicación
 * Configura las rutas principales usando React Router
 * Envuelve la app con AuthProvider para tener acceso a autenticación en toda la app
 */
function App() {
  return (
    <AuthProvider>
      <BusinessSettingsProvider>
        <BusinessRulesProvider>
          <NotificationsSettingsProvider>
            <CartProvider>
              <Router>
                <div className="App">
                  <Routes>
                <Route path="/" element={<PublicLayout />}>
                  <Route index element={<PublicHome />} />
                  <Route path="login" element={<Login />} />
                  <Route path="productos" element={<PublicProducts />} />
                  <Route path="productos/:id" element={<PublicProductDetail />} />
                  <Route path="producto/:id" element={<PublicProductDetail />} />
                  <Route path="ofertas" element={<PublicOffers />} />
                  <Route path="contacto" element={<PublicContact />} />
                  <Route path="carrito" element={<PublicCart />} />
                  <Route path="checkout" element={<PublicCheckout />} />
                  <Route path="checkout/confirmacion/:orderId" element={<OrderConfirmation />} />
                  <Route path="mis-pedidos" element={<MyOrders />} />
                  <Route path="mis-pedidos/:id" element={<MyOrderDetail />} />
                  <Route path="perfil" element={<PublicProfile />} />
                  <Route path="perfil-cliente" element={<CustomerProfile />} />
                  <Route path="ayuda" element={<Help />} />
                </Route>
              
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute>
                    <AdminLayout>
                      <Routes>
                        <Route index element={<AdminDashboard />} />
                        <Route path="productos" element={<AdminProducts />} />
                        <Route path="productos/nuevo" element={<ProductFormRouter />} />
                        <Route path="productos/:id" element={<ProductFormRouter />} />
                        <Route path="productos/:id/historial-precios" element={<ProductPriceHistory />} />
                        <Route path="ofertas" element={<AdminOffers />} />
                        <Route path="ofertas/nuevo" element={<OfferForm />} />
                        <Route path="ofertas/:id" element={<OfferDetail />} />
                        <Route path="ofertas/:id/editar" element={<OfferForm />} />
                        <Route path="pedidos" element={<AdminOrders />} />
                        <Route path="pedidos/:id" element={<OrderDetail />} />
                        <Route path="ventas" element={<AdminSales />} />
                        <Route path="ventas/nuevo/:orderId" element={<SaleForm />} />
                        <Route path="ventas/:id" element={<SaleDetail />} />
                        <Route path="clientes" element={<AdminCustomers />} />
                        <Route path="clientes/:email" element={<CustomerDetail />} />
                        <Route path="usuarios" element={<AdminUsers />} />
                        <Route path="usuarios/nuevo" element={<UserForm />} />
                        <Route path="usuarios/:id" element={<UserForm />} />
                        <Route 
                          path="seguridad" 
                          element={
                            <ProtectedRoute allowedRoles={['superadmin']}>
                              <AdminSecurity />
                            </ProtectedRoute>
                          } 
                        />
                        <Route path="contabilidad" element={<AdminAccounting />} />
                        <Route path="depositos" element={<AdminWarehouses />} />
                        <Route path="depositos/nuevo" element={<WarehouseForm />} />
                        <Route path="depositos/:id" element={<WarehouseForm />} />
                        <Route path="stock" element={<AdminStock />} />
                        <Route path="stock/movimientos" element={<StockMovements />} />
                        <Route path="stock/transferencias" element={<StockTransfer />} />
                        <Route path="configuraciones" element={<AdminSettings />} />
                        <Route path="configuraciones/categorias" element={<AdminCategories />} />
                        <Route path="configuraciones/subcategorias" element={<AdminSubcategories />} />
                        <Route path="configuraciones/envio" element={<AdminShipping />} />
                        <Route path="configuraciones/ganancia" element={<AdminProfitSettings />} />
                        <Route path="configuraciones/ayuda" element={<AdminHelpSettings />} />
                        <Route path="configuraciones/inicio" element={<AdminHomeSettings />} />
                        <Route path="configuraciones/comercial" element={<AdminBusinessSettings />} />
                        <Route path="configuraciones/reglas-negocio" element={<AdminBusinessRules />} />
                        <Route path="configuraciones/notificaciones" element={<AdminNotificationsSettings />} />
                        <Route path="configuraciones/auditoria" element={<AdminAuditLogs />} />
                        <Route path="configuraciones/datos" element={<AdminDataManagement />} />
                        <Route path="proveedores" element={<AdminSuppliers />} />
                        <Route path="proveedores/nuevo" element={<SupplierForm />} />
                        <Route path="proveedores/:email" element={<SupplierDetail />} />
                        <Route path="proveedores/:email/editar" element={<SupplierForm />} />
                        <Route path="configuraciones/alertas" element={<AdminAlertsSettings />} />
                        <Route path="pedidos-compra" element={<AdminPurchaseOrders />} />
                        <Route path="pedidos-compra/nuevo" element={<PurchaseOrderForm />} />
                        <Route path="pedidos-compra/:id" element={<PurchaseOrderDetail />} />
                        <Route path="pedidos-compra/:id/editar" element={<PurchaseOrderForm />} />
                        <Route path="pedidos-compra/:id/pago/:supplierId" element={<RegisterPayment />} />
                        <Route path="insumos" element={<AdminSuppliesList />} />
                        <Route path="insumos/nuevo" element={<SupplyForm />} />
                        <Route path="insumos/:id" element={<SupplyForm />} />
                        <Route path="etiquetas" element={<AdminLabels />} />
                      </Routes>
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              
                  </Routes>
                </div>
              </Router>
            </CartProvider>
          </NotificationsSettingsProvider>
        </BusinessRulesProvider>
      </BusinessSettingsProvider>
    </AuthProvider>
  )
}

export default App
